import { create } from 'zustand';
import { PaymentTypeStr } from '../common/enums';
import LocalStorageSvc, { STAFF_ORDER_KEY } from '../common/localStorageSvc';
import { getOrderItemKey } from '../common/orderFunctions';
import { Order, OrderItem, Product } from '../common/types';
import useAlert from './useAlert';

const emptyOrder = (): Order => ({
    id: 0,
    startTime: null,
    finishTime: null,
    isPaid: false,
    siteId: 0,
    stationId: 0,
    orderItems: [],
    requestedPickupTime: new Date(),
    isDraft: false,
    orderPayments: [],
});

type StaffOrderingState = {
    orderData: Order,
    init: () => void,
    updateOrder: (order: Order) => void,
    clearOrder: () => void,
    setPaymentType: (val: PaymentTypeStr) => void,
    removeOrderItem: (id: number) => void,
    addOrderItem: (item: OrderItem) => void,
    onSizeChange: (id: number, newSizeId: number, products: Product[]) => void,
    onStationChange: (stationId: number) => void,
    onVariationChange: (updatedOrderItem: OrderItem) => void,
}

export const useStaffOrdering = create<StaffOrderingState>((set, get) => ({
    orderData: emptyOrder(),
    init: () => {
        const orderData = LocalStorageSvc.load<Order>(STAFF_ORDER_KEY) || emptyOrder()
        set({ orderData })
    },
    updateOrder: (orderData: Order) => {
        set({ orderData })
        LocalStorageSvc.remove(STAFF_ORDER_KEY)
        LocalStorageSvc.save(STAFF_ORDER_KEY, orderData)
    },
    addOrderItem: (item: OrderItem) => {
        const newItem: OrderItem = {
            ...item,
            id: new Date().getTime() * -1,
            selectedVariants: [...item.selectedVariants],
        };
        const { updateOrder, orderData } = get()
        updateOrder({ ...orderData, orderItems: [...orderData.orderItems, newItem] })
    },
    removeOrderItem: (id: number) => {
        const { updateOrder, orderData } = get()
        const filteredOrderItems = orderData.orderItems.filter(
            (item) => item.id !== id,
        );
        updateOrder({ ...orderData, orderItems: filteredOrderItems });
    },
    clearOrder: () => {
        get().updateOrder(emptyOrder())
    },
    setPaymentType: (val: PaymentTypeStr) => {
        const { updateOrder, orderData } = get()
        updateOrder({ ...orderData, paymentType: val })
    },
    onSizeChange: (id: number, newSizeId: number, products: Product[] = []) => {
        const { updateOrder, orderData } = get()
        const orderItem = orderData.orderItems.find((item) => item.id === id);
        if (!orderItem) return;

        const newProductSize = products
            .find((product) => product.id === orderItem.productId)
            ?.productSizes.find((productSize) => productSize.id === newSizeId);

        if (!newProductSize) {
            useAlert.getState().showAlert('Failed to assign size, try again', 'warning');
            console.error('Failed to assign size, try again');
            return;
        }

        const key = getOrderItemKey(orderItem);
        const currentGroup = orderData.orderItems.filter(
            (x) => key === getOrderItemKey(x),
        );

        const updatedOrderItems = orderData.orderItems.map((item) =>
            currentGroup.some((groupItem) => groupItem.id === item.id)
                ? {
                    ...item,
                    productSizeId: newProductSize.id,
                }
                : item,
        );

        updateOrder({ ...orderData, orderItems: updatedOrderItems });
    },
    onStationChange: (stationId: number) => {
        const { updateOrder, orderData } = get()
        updateOrder({ ...orderData, stationId })
    },
    onVariationChange: (updatedOrderItem: OrderItem, products: Product[] = []) => {
        const { updateOrder, onSizeChange, orderData } = get()

        const existingItem = orderData.orderItems.find(
            f => f.id === updatedOrderItem.id
        )

        if (!existingItem) {
            const updatedOrderItems = [...orderData.orderItems, { ...updatedOrderItem, id: new Date().getTime() * -1 }]
            updateOrder({ ...orderData, orderItems: updatedOrderItems });
            return
        }

        const key = getOrderItemKey(existingItem);
        const currentGroup = orderData.orderItems.filter(
            (x) => key === getOrderItemKey(x),
        );

        const updatedOrderItems = orderData.orderItems.map((item) => {
            if (currentGroup.some((groupItem) => groupItem.id === item.id)) {
                return {
                    ...item,
                    selectedVariants: updatedOrderItem.selectedVariants,
                    notes: updatedOrderItem.notes,
                };
            }
            return item;
        });

        updateOrder({ ...orderData, orderItems: updatedOrderItems });

        if (existingItem.productSizeId !== updatedOrderItem.productSizeId) {
            currentGroup.forEach((groupItem) => {
                onSizeChange(groupItem.id, updatedOrderItem.productSizeId, products);
            });
        }
    },
}))

export default useStaffOrdering